import { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Grid3X3, Settings, Code2, Shield, Navigation, FileText, CreditCard, Sparkles } from 'lucide-react';
import { usePaulAccess } from '@/lib/paulAccess';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const OPENAI_API_KEY_STORAGE = 'appsations.openai.apiKey';
const OPENAI_MODEL_STORAGE = 'appsations.openai.model';
const DEFAULT_OPENAI_MODEL = 'gpt-4o-mini';

const AppAdmin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('plans');
  const [openAiApiKey, setOpenAiApiKey] = useState('');
  const [openAiModel, setOpenAiModel] = useState(DEFAULT_OPENAI_MODEL);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const { isPaul, isReady } = usePaulAccess();
  const developerUnlocked = isPaul;

  const navItems = useMemo(() => {
    const items = [
      { id: 'dashboard', label: 'Dashboard', icon: <Grid3X3 className="w-4 h-4" />, to: '/admin' },
      { id: 'appadmin', label: 'App Admin', icon: <Settings className="w-4 h-4" />, to: '/appadmin' },
      { id: 'plans', label: 'Plans', icon: <Navigation className="w-4 h-4" />, to: '/plans' },
    ];

    if (developerUnlocked) {
      items.push({ id: 'roles', label: 'User roles', icon: <Shield className="w-4 h-4" />, to: '/roles' });
      items.push({ id: 'developer', label: 'Developer', icon: <Code2 className="w-4 h-4" />, to: '/developer' });
    }

    return items;
  }, [developerUnlocked]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      setOpenAiApiKey(window.localStorage.getItem(OPENAI_API_KEY_STORAGE) ?? '');
      setOpenAiModel(window.localStorage.getItem(OPENAI_MODEL_STORAGE) ?? DEFAULT_OPENAI_MODEL);
    } catch {
      setOpenAiApiKey('');
      setOpenAiModel(DEFAULT_OPENAI_MODEL);
    }
  }, []);

  useEffect(() => {
    if (!isReady) return;
    if (!isPaul) {
      navigate('/');
      return;
    }

    if (!developerUnlocked && activeTab === 'roles') {
      setActiveTab('plans');
    }
  }, [activeTab, developerUnlocked, isPaul, isReady, navigate]);

  const saveOpenAiSettings = () => {
    if (typeof window === 'undefined') return;

    const trimmedKey = openAiApiKey.trim();
    const trimmedModel = openAiModel.trim() || DEFAULT_OPENAI_MODEL;

    try {
      if (trimmedKey) {
        window.localStorage.setItem(OPENAI_API_KEY_STORAGE, trimmedKey);
      } else {
        window.localStorage.removeItem(OPENAI_API_KEY_STORAGE);
      }
      window.localStorage.setItem(OPENAI_MODEL_STORAGE, trimmedModel);
    } catch {
      // Storage can be unavailable in hardened browser contexts.
    }
    window.dispatchEvent(new Event('openai-settings-changed'));
    setSavedAt(new Date().toLocaleTimeString());
  };

  return (
    <div className="h-[100dvh] bg-background nebula-bg star-field flex flex-col overflow-hidden">
      <Header navItems={navItems} />

      <main className="container mx-auto px-4 pt-4 pb-[calc(3.5rem+var(--safe-area-bottom,0px))] md:pb-8 flex-1 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto space-y-8">
          {/* Page Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-display font-bold">App Admin</h1>
            <p className="text-muted-foreground">
              Manage application-wide settings, payments, and data
            </p>
          </div>

          <div className="rounded-lg border border-border/60 bg-card/50 backdrop-blur p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <h2 className="text-lg font-semibold">AI Setup</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Configure OpenAI access used by the AI day reading widget.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">OpenAI API Key</label>
                <input
                  type="password"
                  value={openAiApiKey}
                  onChange={(event) => setOpenAiApiKey(event.target.value)}
                  placeholder="sk-..."
                  className="mt-1 w-full px-3 py-2 rounded-md border border-border/40 bg-background/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="text-sm font-medium">OpenAI Model</label>
                <input
                  type="text"
                  value={openAiModel}
                  onChange={(event) => setOpenAiModel(event.target.value)}
                  placeholder={DEFAULT_OPENAI_MODEL}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-border/40 bg-background/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button type="button" onClick={saveOpenAiSettings}>Save AI Settings</Button>
              {savedAt ? <span className="text-xs text-muted-foreground">Saved at {savedAt}</span> : null}
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-2">
              <TabsTrigger value="plans" className="flex items-center gap-2">
                <Navigation className="w-4 h-4" />
                <span className="hidden sm:inline">Plans</span>
              </TabsTrigger>
              <TabsTrigger value="payments" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span className="hidden sm:inline">Payments</span>
              </TabsTrigger>
              <TabsTrigger value="reports" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Reports</span>
              </TabsTrigger>
              {developerUnlocked && (
                <TabsTrigger value="roles" className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Roles</span>
                </TabsTrigger>
              )}
            </TabsList>

            {/* Plans Tab */}
            <TabsContent value="plans" className="space-y-6">
              <div className="rounded-lg border border-border/60 bg-card/50 backdrop-blur p-6">
                <h2 className="text-xl font-semibold mb-4">Subscription Plans</h2>
                <p className="text-muted-foreground mb-4">
                  Configure and manage subscription plans for your users.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-border/40 p-4 space-y-3">
                    <h3 className="font-medium">Free Plan</h3>
                    <p className="text-sm text-muted-foreground">
                      Basic features for getting started
                    </p>
                    <Button variant="outline" size="sm">
                      Edit Plan
                    </Button>
                  </div>
                  <div className="rounded-lg border border-border/40 p-4 space-y-3">
                    <h3 className="font-medium">Pro Plan</h3>
                    <p className="text-sm text-muted-foreground">
                      Advanced features and priority support
                    </p>
                    <Button variant="outline" size="sm">
                      Edit Plan
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments" className="space-y-6">
              <div className="rounded-lg border border-border/60 bg-card/50 backdrop-blur p-6">
                <h2 className="text-xl font-semibold mb-4">Payment Settings</h2>
                <p className="text-muted-foreground mb-6">
                  Configure payment providers and settings.
                </p>

                {/* Square Setup */}
                <div className="space-y-4">
                  <div className="rounded-lg border border-border/40 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium mb-1">Square Integration</h3>
                        <p className="text-sm text-muted-foreground">
                          Connect your Square account for payment processing
                        </p>
                      </div>
                      <div className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-700 text-xs font-medium">
                        Not Connected
                      </div>
                    </div>
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="text-sm font-medium">Application ID</label>
                        <input
                          type="text"
                          placeholder="Enter your Square Application ID"
                          className="mt-1 w-full px-3 py-2 rounded-md border border-border/40 bg-background/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Access Token</label>
                        <input
                          type="password"
                          placeholder="Enter your Square Access Token"
                          className="mt-1 w-full px-3 py-2 rounded-md border border-border/40 bg-background/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                      </div>
                      <Button className="w-full">
                        Connect Square Account
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports" className="space-y-6">
              <div className="rounded-lg border border-border/60 bg-card/50 backdrop-blur p-6">
                <h2 className="text-xl font-semibold mb-4">Reports & Analytics</h2>
                <p className="text-muted-foreground mb-6">
                  View application metrics and user statistics.
                </p>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-lg border border-border/40 p-4 space-y-2">
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-2xl font-bold">—</p>
                  </div>
                  <div className="rounded-lg border border-border/40 p-4 space-y-2">
                    <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                    <p className="text-2xl font-bold">—</p>
                  </div>
                  <div className="rounded-lg border border-border/40 p-4 space-y-2">
                    <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                    <p className="text-2xl font-bold">—</p>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    Export User Data
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    Export Revenue Report
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Roles Tab */}
            <TabsContent value="roles" className="space-y-6">
              <div className="rounded-lg border border-border/60 bg-card/50 backdrop-blur p-6">
                <h2 className="text-xl font-semibold mb-4">User Roles & Permissions</h2>
                <p className="text-muted-foreground mb-6">
                  Manage user roles and their permissions.
                </p>

                <div className="space-y-3">
                  <div className="rounded-lg border border-border/40 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium">Admin</h3>
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary">
                        Full Access
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Full access to all admin features and settings
                    </p>
                    <Button variant="outline" size="sm">
                      Edit Permissions
                    </Button>
                  </div>

                  <div className="rounded-lg border border-border/40 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium">User</h3>
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary/20 text-secondary">
                        Limited
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Standard user access with personal dashboard
                    </p>
                    <Button variant="outline" size="sm">
                      Edit Permissions
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default AppAdmin;
